// Fix prettier/eslint conflicts for the opening brace after export class...
/* eslint-disable brace-style */
import { Component, OnInit, inject } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AbstractDialogResultData, AbstractFormDialog } from '@next/core-lib/dialog';
import { Store } from '@ngrx/store';
import { CropField, FieldsDialogModel, GrasslandUsage, SiteState } from 'src/app/shared/models';
import * as AppActionTypes from 'src/app/store/actions';

import { PostManureSpaceSettingsData } from '../models/manure-space-settings.models';

interface FieldsDialogData {
  manureSpaceSettings: PostManureSpaceSettingsData[];
}

interface FilteredCropField {
  cropField: CropField;
  index: number;
}

@Component({
  selector: 'app-fields-quick-edit',
  templateUrl: './fields-quick-edit.component.html',
  styleUrls: ['./fields-quick-edit.component.scss'],
  standalone: false,
})
export class FieldsQuickEditComponent
  extends AbstractFormDialog<FieldsDialogData>
  implements OnInit
{
  data = inject<FieldsDialogModel>(MAT_DIALOG_DATA);
  protected readonly mdDialogRef =
    inject<MatDialogRef<FieldsQuickEditComponent, AbstractDialogResultData<FieldsDialogData>>>(
      MatDialogRef,
    );
  private readonly store = inject<Store<SiteState>>(Store);

  private initialManureSpaceSettings: PostManureSpaceSettingsData[] = [];
  private readonly loadedFieldIndices: Set<number> = new Set<number>();
  public readonly searchControl = new FormControl('');
  public readonly manureSpaceSettings = new FormArray([], [Validators.required]);
  public readonly formGroup = new FormGroup({
    manureSpaceSettings: this.manureSpaceSettings,
  });

  ngOnInit() {
    this.loadCropVarieties(this.data.cropFields.map((f) => f.cropId));

    this.data.cropFields.forEach(() => {
      this.addFieldSettingsFormGroup();
    });
  }

  private getFormGroupValues(formArray: FormArray): PostManureSpaceSettingsData[] {
    return formArray.controls.map((control) => {
      const formGroup = control as FormGroup;
      return formGroup.value;
    });
  }

  onFieldDataLoaded(index: number): void {
    this.loadedFieldIndices.add(index);
    if (this.loadedFieldIndices.size === this.data.cropFields.length) {
      this.initialManureSpaceSettings = this.getFormGroupValues(this.manureSpaceSettings);
    }
  }

  confirmEditFieldSettings({ manureSpaceSettings }: FieldsDialogData) {
    const changedSettings = manureSpaceSettings.filter(
      (setting, index) =>
        setting.id !== null &&
        setting.id !== undefined &&
        this.hasSettingsChanged(setting, this.initialManureSpaceSettings[index]),
    );

    if (changedSettings.length > 0) {
      this.confirm({ manureSpaceSettings: changedSettings });
    } else {
      this.cancelEditFieldSettings();
    }
  }

  private hasSettingsChanged(
    current: PostManureSpaceSettingsData,
    initial: PostManureSpaceSettingsData | undefined,
  ): boolean {
    if (!initial) {
      return true;
    }
    if (
      current.shouldBeUsedInLegalStandard !== initial.shouldBeUsedInLegalStandard ||
      current.shouldUseTransitionalArrangement !== initial.shouldUseTransitionalArrangement ||
      current.shouldUseLowInPhosphateArrangement !== initial.shouldUseLowInPhosphateArrangement
    ) {
      return true;
    }
    const currentNitrogen = current.nitrogenCalculationSettings;
    const initialNitrogen = initial.nitrogenCalculationSettings;

    return (
      currentNitrogen.isPreviousCropCorn !== initialNitrogen.isPreviousCropCorn ||
      currentNitrogen.isPreviousCropNotGrain !== initialNitrogen.isPreviousCropNotGrain ||
      currentNitrogen.shouldSplitNorm !== initialNitrogen.shouldSplitNorm ||
      currentNitrogen.isNitrogenDifferentiation !== initialNitrogen.isNitrogenDifferentiation ||
      currentNitrogen.isAfterTornGrass !== initialNitrogen.isAfterTornGrass ||
      currentNitrogen.grasslandUsage !== initialNitrogen.grasslandUsage ||
      currentNitrogen.isRecoveryFertilisation !== initialNitrogen.isRecoveryFertilisation
    );
  }

  cancelEditFieldSettings() {
    this.cancel();
  }

  trackByFn(index: number) {
    return index;
  }

  get filteredCropFields(): FilteredCropField[] {
    const searchTerm = this.searchControl.value?.trim().toLowerCase() ?? '';

    return this.data.cropFields
      .map((cropField, index) => ({ cropField, index }))
      .filter(({ cropField }) => this.matchesSearchTerm(cropField, searchTerm));
  }

  private matchesSearchTerm(cropField: CropField, searchTerm: string): boolean {
    const cropName =
      this.data.crops
        .find((crop) => crop.id === cropField.cropId)
        ?.agrifirmCrop.name.toLowerCase() ?? '';

    return cropField.name.toLowerCase().includes(searchTerm) || cropName.includes(searchTerm);
  }

  addFieldSettingsFormGroup() {
    const nitrogenCalculationSettings = new FormGroup({
      isPreviousCropCorn: new FormControl(false),
      isPreviousCropNotGrain: new FormControl(false),
      shouldSplitNorm: new FormControl(false),
      isNitrogenDifferentiation: new FormControl(false),
      isAfterTornGrass: new FormControl(false),
      grasslandUsage: new FormControl(GrasslandUsage.Graze),
      isRecoveryFertilisation: new FormControl(false),
    });
    const formGroup = new FormGroup({
      id: new FormControl(null),
      shouldBeUsedInLegalStandard: new FormControl(false),
      shouldUseTransitionalArrangement: new FormControl(false),
      shouldUseLowInPhosphateArrangement: new FormControl(false),
      nitrogenCalculationSettings,
    });
    this.manureSpaceSettings.push(formGroup);
  }

  private loadCropVarieties(cropIds: number[]) {
    if (cropIds.length > 0) {
      this.store.dispatch(AppActionTypes.LoadCropVarietiesByCropIdsRequest({ cropIds }));
    }
  }

  getFormGroup(index: number, formArray: FormArray) {
    return formArray.controls[index] as FormGroup;
  }
}
